import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import { useSelector } from "react-redux";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import HandshakeTwoToneIcon from "@mui/icons-material/HandshakeTwoTone";
import DescriptionIcon from "@mui/icons-material/DescriptionTwoTone";
// @ts-expect-error: ignoring as it is existing js file
import { applicationSelector, assetSelector, userDetailsSelector } from "@/store/slices/applicationFormSlice";
import type {
  ApplicationFormSlice,
  ApplicationState,
  Asset,
  Customer,
  Entity,
} from "@/store/slices/types/applicationFormSlice";
import {
  APPLICATION_SECTIONS_ID,
  ROUTES,
  getIsAssetLoan,
  getIsCommercialOrBusinessApplication,
  getIsConsumerOrPersonalApplication,
} from "@/constants";
import { useDemoCommercialApplication } from "@/hooks";
import { useFeatureFlags } from "@/utils/featureFlags";
// @ts-expect-error: ignoring as it is existing js file
import { getCustomerRole } from "@/components/Utils/helperFunction";
import type { SidebarNavItem, SidebarNavChildItem } from "./types";

const useSidebarNavItems = (): SidebarNavItem[] => {
  const isQuotePage = !!useMatch(`/${ROUTES.APP.QUOTE_DETAIL}`);
  const application = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const asset = useSelector<ApplicationFormSlice, Asset>(assetSelector);
  const userDetails = useSelector(userDetailsSelector) as {
    application?: ApplicationState;
    [key: string]: unknown;
  };
  const { isDemoCommercialApplication } = useDemoCommercialApplication();
  const { isApplicationFormAlternativeLayout } = useFeatureFlags();
  const isAlternativeLayout = isApplicationFormAlternativeLayout();

  const applicationType = application?.applicationType;
  const isAssetLoan = applicationType ? getIsAssetLoan(applicationType) : false;
  const isCommercialOrBusiness = applicationType ? getIsCommercialOrBusinessApplication(applicationType) : false;
  const isConsumerOrPersonal = applicationType ? getIsConsumerOrPersonalApplication(applicationType) : false;
  const customerRole = getCustomerRole(applicationType, application?.entities || [], application?.purpose);

  const { ASSETS, LOAN_DETAILS, INTRODUCER_DECLARATION, LOAN_SUMMARY } = APPLICATION_SECTIONS_ID;

  const navItems = useMemo<SidebarNavItem[]>(() => {
    const items: SidebarNavItem[] = [];

    // Build reusable nav item definitions
    const allAssets: Asset[] = [];
    if (asset && typeof asset === "object" && "_id" in asset) {
      allAssets.push(asset);
    }
    if (userDetails?.application?.additionalAssets?.length) {
      allAssets.push(...userDetails.application.additionalAssets);
    }

    const assetChildren: SidebarNavChildItem[] = allAssets.map((assetItem, index) => ({
      id: `asset-${assetItem._id || index}`,
      label: assetItem.assetType || `Asset ${index + 1}`,
      sectionId: `asset-${assetItem._id || index}`,
    }));

    const assetsItem: SidebarNavItem = {
      id: "assets",
      label: "Assets",
      icon: <DirectionsCarIcon />,
      badgeCount: allAssets.length,
      sectionId: ASSETS,
      children: assetChildren,
    };

    const loanSummaryItem: SidebarNavItem = {
      id: "loanSummary",
      label: "Summary",
      icon: <DescriptionIcon />,
      sectionId: LOAN_SUMMARY,
    };

    const loanDetailsItem: SidebarNavItem = {
      id: "loanDetails",
      label: "Loan Details",
      icon: <RequestQuoteIcon />,
      sectionId: LOAN_DETAILS,
    };

    const entities: Entity[] = (application?.entities as Entity[]) || [];
    const entityChildren: SidebarNavChildItem[] = entities.map((entity, index) => ({
      id: `entity-${entity._id || index}`,
      label: entity.entityName || `Entity ${index + 1}`,
      sectionId: `entity-${entity._id}`,
    }));

    const entitiesItem: SidebarNavItem = {
      id: "entities",
      label: "Entity",
      icon: <BusinessIcon />,
      badgeCount: entities.length,
      sectionId: entities.length > 0 ? `entity-${entities[0]._id}` : "addEntity",
      children: entityChildren,
    };

    const customers: Customer[] = (application?.customers as Customer[]) || [];
    const customerChildren: SidebarNavChildItem[] = customers.map((customer, index) => {
      const hasName = customer?.firstName || customer?.lastName;
      const displayName = hasName
        ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim()
        : `${customerRole} ${index + 1}`;

      return {
        id: `customer-${customer._id || index}`,
        label: displayName,
        sectionId: `customer-${customer._id || index}`,
      };
    });

    const customersItem: SidebarNavItem = {
      id: "customers",
      label: customerRole,
      icon: <PersonIcon />,
      badgeCount: customers.length,
      sectionId: customers.length > 0 ? `customer-${customers[0]._id}` : `add${customerRole}`,
      children: customerChildren,
    };

    const nccpItem: SidebarNavItem = {
      id: "nccp",
      label: "NCCP",
      icon: <HandshakeTwoToneIcon />,
      sectionId: INTRODUCER_DECLARATION,
    };

    if (isAlternativeLayout) {
      // Alternative layout order matches ApplicationFormAlternativeLayout:
      // Commercial/Business: Entities → Assets → Loan Details → Customers
      // Consumer/Personal:   Customers → Assets → Loan Details → NCCP
      if (isCommercialOrBusiness) {
        items.push(entitiesItem);
        if (!isDemoCommercialApplication && isAssetLoan) items.push(assetsItem);
        items.push(loanDetailsItem);
        items.push(customersItem);
      } else {
        if (isDemoCommercialApplication) items.push(loanSummaryItem);
        items.push(customersItem);
        if (!isDemoCommercialApplication && isAssetLoan) items.push(assetsItem);
        items.push(loanDetailsItem);
        if (isConsumerOrPersonal && !isQuotePage) items.push(nccpItem);
      }
    } else {
      // Standard layout order
      if (!isDemoCommercialApplication && isAssetLoan) items.push(assetsItem);
      if (isDemoCommercialApplication) items.push(loanSummaryItem);
      items.push(loanDetailsItem);
      if (isCommercialOrBusiness) items.push(entitiesItem);
      items.push(customersItem);
      if (isConsumerOrPersonal && !isQuotePage) items.push(nccpItem);
    }

    return items;
  }, [
    isAlternativeLayout,
    isDemoCommercialApplication,
    isAssetLoan,
    isCommercialOrBusiness,
    isConsumerOrPersonal,
    isQuotePage,
    asset,
    userDetails?.application?.additionalAssets,
    application?.entities,
    application?.customers,
    customerRole,
    ASSETS,
    LOAN_DETAILS,
    LOAN_SUMMARY,
    INTRODUCER_DECLARATION,
  ]);

  return navItems;
};

export { useSidebarNavItems };
